'use strict';

import { findMatchingSchema } from './routePatternMatcher.js';
import { TranslateJSON } from './bfUtilities.js';

const PREVIEW_STORAGE_KEY = 'bf_global_schema_preview'; // GLOBAL_SCHEMA_DEV_PREVIEW: remove with dev preview tooling
const TEST_MODE_STORAGE_KEY = 'bf_global_schema_test_mode'; // GLOBAL_SCHEMA_DEV_PREVIEW

const VALID_TEST_MODES = new Set(['legacy', 'global']);

function resolveGlobalLayoutSchema(globalSchema, route) {
  if (!globalSchema || typeof globalSchema !== 'object') {
    return null;
  }

  if (Array.isArray(globalSchema.fields) && globalSchema.fields.length) {
    return globalSchema;
  }

  if (globalSchema.default || globalSchema.routes) {
    const routePath = route && route.path ? route.path : route;
    return findMatchingSchema(globalSchema, routePath);
  }

  return null;
}

function parseToggleOverride(queryValue) {
  if (queryValue === '1') return true;
  if (queryValue === '0') return false;
  return null;
}

function isGlobalSchemaLayoutEnabled(content) {
  return Boolean(
    content &&
    content.layout &&
    content.layout.enableGlobalSchema
  );
}

function shouldUseGlobalSchemaLayout(content, queryOverride, testMode) {
  // GLOBAL_SCHEMA_DEV_PREVIEW: testMode + queryOverride are temporary dial-in paths; keep layout flag branch in production.
  const globalSchema = content && content.globalSchema;
  const hasSchema = Boolean(
    globalSchema &&
    typeof globalSchema === 'object' &&
    Object.keys(globalSchema).length > 0
  );

  if (!hasSchema) return false;

  if (testMode === 'legacy') return false;
  if (testMode === 'global') return true;

  const override = parseToggleOverride(queryOverride);
  if (override === false) return false;
  if (override === true) return true;

  return isGlobalSchemaLayoutEnabled(content);
}

/**
 * Inner #page.main-content-wrapper padding under the absolute Site Header.
 * When global schema layout is active, Page Body / Secondary Header own that
 * offset via styleClasses — do not add a second 60px on #page.
 */
function resolveFormFramePaddingTop({ idWndw, showTopNavBar, useGlobalSchemaLayout }) {
  if (idWndw || !showTopNavBar || useGlobalSchemaLayout) return 0;
  return '';
}

function normalizeTestMode(testMode) {
  return VALID_TEST_MODES.has(testMode) ? testMode : null;
}

function isGlobalSchemaLayoutTestHost(hostname) {
  return hostname === 'appdev.klai.studio' || hostname === 'christinadev.bfoperations.com';
}

function resolveGlobalSchemaTestMode(sessionMode) {
  return normalizeTestMode(sessionMode);
}

function readTestModeFromSession() {
  try {
    return normalizeTestMode(window.sessionStorage.getItem(TEST_MODE_STORAGE_KEY));
  } catch (e) {
    return null;
  }
}

function writeTestModeToSession(testMode) {
  try {
    const normalized = normalizeTestMode(testMode);
    if (!normalized) {
      window.sessionStorage.removeItem(TEST_MODE_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(TEST_MODE_STORAGE_KEY, normalized);
  } catch (e) {
    // ignore quota errors during local preview
  }
}

function clearTestModeFromSession() {
  try {
    window.sessionStorage.removeItem(TEST_MODE_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

function getGlobalSchemaTestStatus(content, queryOverride, testMode) {
  const hasSchema = Boolean(
    content &&
    content.globalSchema &&
    typeof content.globalSchema === 'object' &&
    Object.keys(content.globalSchema).length > 0
  );
  const activeLayout = shouldUseGlobalSchemaLayout(content, queryOverride, testMode)
    ? 'global'
    : 'legacy';

  return {
    testMode: normalizeTestMode(testMode),
    queryOverride: parseToggleOverride(queryOverride),
    hasPreviewSchema: hasSchema,
    activeLayout
  };
}

function buildGlobalSchemaToggleUrl(forceOn) {
  const href = window.location.href;
  const hashIndex = href.indexOf('#');
  const base = hashIndex >= 0 ? href.slice(0, hashIndex + 1) : `${href}#`;
  const hashPart = hashIndex >= 0 ? href.slice(hashIndex + 1) : '/';
  const queryIndex = hashPart.indexOf('?');
  const hashPath = queryIndex >= 0 ? hashPart.slice(0, queryIndex) : hashPart;
  const query = queryIndex >= 0 ? hashPart.slice(queryIndex + 1) : '';
  const params = new URLSearchParams(query);

  params.set('_forceGlobalSchema', forceOn ? '1' : '0');

  const nextQuery = params.toString();
  return `${base}${hashPath}${nextQuery ? `?${nextQuery}` : ''}`;
}

function readPreviewSchemaFromSession() {
  try {
    const raw = window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writePreviewSchemaToSession(globalSchema) {
  try {
    window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(globalSchema));
  } catch (e) {
    // ignore quota errors during local preview
  }
}

function clearPreviewSchemaFromSession() {
  try {
    window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

function restoreGlobalSchemaPreview(content) {
  if (!content || content.globalSchema) return content;

  const previewSchema = readPreviewSchemaFromSession();
  if (!previewSchema) return content;

  return Object.assign({}, content, {
    globalSchema: prepareGlobalSchemaForRuntime(previewSchema)
  });
}

function prepareGlobalSchemaForRuntime(globalSchema) {
  if (!globalSchema || typeof globalSchema !== 'object') {
    return globalSchema;
  }

  const clone = JSON.parse(JSON.stringify(globalSchema));
  TranslateJSON(clone, 0);
  return clone;
}

export {
  PREVIEW_STORAGE_KEY,
  TEST_MODE_STORAGE_KEY,
  resolveGlobalLayoutSchema,
  isGlobalSchemaLayoutEnabled,
  shouldUseGlobalSchemaLayout,
  resolveFormFramePaddingTop,
  buildGlobalSchemaToggleUrl,
  readPreviewSchemaFromSession,
  writePreviewSchemaToSession,
  clearPreviewSchemaFromSession,
  restoreGlobalSchemaPreview,
  prepareGlobalSchemaForRuntime,
  readTestModeFromSession,
  writeTestModeToSession,
  clearTestModeFromSession,
  getGlobalSchemaTestStatus,
  isGlobalSchemaLayoutTestHost,
  resolveGlobalSchemaTestMode
};
